import React, { ReactNode, Ref, useMemo } from 'react';
import SelectBox, { Props } from 'react-select';
import { GroupBase } from 'react-select/dist/declarations/src/types';

import cx from 'clsx';

import { SelectOption } from '../../types';

import { ClearIndicator } from './components/Indicators/ClearIndicator';
import { Control } from './components/Indicators/Control';
import { DropdownIndicator } from './components/Indicators/DropdownIndicator';
import { mixClassNames } from './Select.utils';

import styles from './Select.module.scss';

const isOptionDisabled = (option: any) => Boolean((option as SelectOption)?.disabled);

export type SelectProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Props<Option, IsMulti, Group> & {
  valid?: boolean | null;
  prefixContent?: ReactNode;
};

/**
 * This component is used as a standalone select box without need to provide any context and etc.
 */
function InnerSelect<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  {
    className,
    isClearable = true,
    valid,
    components = {},
    classNames,
    isDisabled,
    defaultValue = null,
    ...props
  }: SelectProps<Option, IsMulti, Group>,
  ref: Ref<any>
) {
  const mixedClassNames = useMemo(() => mixClassNames<Option, IsMulti, Group>(classNames || {}), [classNames]);

  return (
    <div className={styles.select__group} data-testid="select-component">
      <SelectBox<Option, IsMulti, Group>
        classNamePrefix="select"
        {...props}
        defaultValue={defaultValue}
        isClearable={isClearable}
        components={{
          ClearIndicator,
          DropdownIndicator,
          Control,
          ...components,
        }}
        className={cx(
          styles.select,
          className,
          isDisabled && styles['select--is-disabled'],
          typeof valid === 'boolean' && styles[`select--${valid ? 'valid' : 'invalid'}`]
        )}
        classNames={mixedClassNames}
        isOptionDisabled={isOptionDisabled}
        ref={ref}
        isDisabled={isDisabled}
      />
    </div>
  );
}

export const Select = React.forwardRef(InnerSelect) as <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  p: SelectProps<Option, IsMulti, Group> & { ref?: Ref<HTMLSelectElement> }
) => ReturnType<typeof InnerSelect<Option, IsMulti, Group>>;
